/**
 * useCopilot — owns the copilot conversation state and the WS transport.
 * Lazily connects on first window-open; renders agent steps from real `trace`
 * frames. Conversation history is SERVER-OWNED and per-user: the drawer lists,
 * loads and deletes past conversations over the gen-ai REST API
 * (lib/historyApi) using the same end-user token as the WS. The ACTIVE session
 * renders from live WS frames (never re-fetched); REST is used only to load a
 * PAST session the user selected. No localStorage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { getJsonCookies } from '@helpers/utility'
import { createCopilotClient, CopilotClient } from './lib/createCopilotClient'
import { reduceTrace, initialSteps, agentsFromFinal } from './lib/agents'
import { widgetsFrom } from './widgets/extract'
import {
  CopilotMessage,
  CopilotStatus,
  CopilotStep,
  ConversationSummary,
  FinalData,
  FinalFrame,
  HistoryErrorKind,
  TraceEvent
} from './lib/copilotTypes'
import {
  listConversations,
  getFullTranscript,
  deleteConversation,
  transcriptToMessages,
  CopilotHistoryError
} from './lib/historyApi'

const uuid = (): string => {
  const c = (typeof crypto !== 'undefined' ? crypto : undefined) as { randomUUID?: () => string } | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `m_${Math.random().toString(36).slice(2)}${Date.now()}`
}

const deriveChip = (data?: FinalData): string | undefined => {
  if (data && Array.isArray(data.subagents_used) && data.subagents_used.length) return data.subagents_used.join(' + ')
  if (data && data.intent && data.intent !== 'unknown') return data.intent
  return undefined
}

const errorKind = (err: unknown): HistoryErrorKind => (err instanceof CopilotHistoryError ? err.kind : 'unknown')

export interface UseCopilot {
  status: CopilotStatus
  messages: CopilotMessage[]
  steps: CopilotStep[]
  thinking: boolean
  error: string | null
  sessions: ConversationSummary[]
  historyLoading: boolean
  historyError: HistoryErrorKind | null
  activeSessionId: string | null
  connect: () => void
  send: (text: string, opts?: { voice?: boolean }) => void
  newChat: () => void
  refreshHistory: () => void
  resumeSession: (sessionId: string) => void
  deleteSession: (sessionId: string) => void
}

export const useCopilot = (): UseCopilot => {
  const [status, setStatus] = useState<CopilotStatus>('idle')
  const [messages, setMessages] = useState<CopilotMessage[]>([])
  const [steps, setSteps] = useState<CopilotStep[]>([])
  const [thinking, setThinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessions, setSessions] = useState<ConversationSummary[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<HistoryErrorKind | null>(null)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)

  const clientRef = useRef<CopilotClient | null>(null)
  const activeSessionRef = useRef<string | null>(null)
  const pendingTitleRef = useRef<string | null>(null)
  const queueRef = useRef<string[]>([])
  const statusRef = useRef<CopilotStatus>('idle')
  statusRef.current = status

  /* Optimistically surface a just-started session in the drawer. Write-behind
     means a new session isn't in GET /conversations immediately; the next
     refreshHistory() reconciles this stub with the real server row. */
  const optimisticInsert = useCallback((sessionId: string, title: string) => {
    setSessions((prev) => {
      if (prev.some((s) => s.session_id === sessionId)) return prev
      const stub: ConversationSummary = { session_id: sessionId, title: title.trim().slice(0, 80) }
      return [stub, ...prev]
    })
  }, [])

  const handleFinal = useCallback((frame: FinalFrame) => {
    setSteps((prev) => {
      const marked = prev.map((s) => ({ ...s, done: true }))
      const hasAgents = marked.some((s) => s.kind === 'agent')
      // if no dispatch traces arrived, backfill the agents that actually ran
      return hasAgents ? marked : [...marked, ...agentsFromFinal(frame.data)]
    })
    setMessages((prev) => [
      ...prev,
      {
        id: uuid(),
        role: 'assistant',
        text: frame.text,
        chip: deriveChip(frame.data),
        widgets: widgetsFrom(frame.data)
      }
    ])
    setThinking(false)
    // clear the step strip shortly after so the checks flash briefly
    setTimeout(() => setSteps([]), 350)
  }, [])

  const handleTrace = useCallback((event: TraceEvent) => {
    // fold each trace into the timeline: Understanding → [agent] as dispatched
    setSteps((prev) => reduceTrace(prev, event))
  }, [])

  const flushQueue = useCallback(() => {
    const q = queueRef.current
    queueRef.current = []
    q.forEach((text) => {
      if (clientRef.current) clientRef.current.send(text, uuid())
    })
  }, [])

  const connect = useCallback(
    (sessionId: string | null = activeSessionRef.current) => {
      if (clientRef.current) return
      const token = getJsonCookies()
      if (!token) {
        setStatus('error')
        setError('copilot-error-auth')
        return
      }
      setError(null)
      clientRef.current = createCopilotClient({
        token,
        sessionId,
        onStatus: setStatus,
        onReady: (sid) => {
          activeSessionRef.current = sid
          setActiveSessionId(sid)
          // a queued first message defines the title before the server row exists
          if (pendingTitleRef.current) {
            optimisticInsert(sid, pendingTitleRef.current)
            pendingTitleRef.current = null
          }
          flushQueue()
        },
        onTrace: handleTrace,
        onFinal: handleFinal,
        onError: (message) => {
          setThinking(false)
          setSteps([])
          setMessages((prev) => [...prev, { id: uuid(), role: 'assistant', text: message || '', chip: 'error' }])
        },
        onAuthFailure: () => {
          setStatus('error')
          setError('copilot-error-auth')
        }
      })
    },
    [flushQueue, handleTrace, handleFinal, optimisticInsert]
  )

  const send = useCallback(
    (text: string, opts: { voice?: boolean } = {}) => {
      const trimmed = text.trim()
      if (!trimmed || thinking) return
      connect(activeSessionRef.current)
      setMessages((prev) => [...prev, { id: uuid(), role: 'user', text: trimmed, voice: !!opts.voice }])
      // show "Understanding your request" immediately, before any trace arrives
      setSteps(initialSteps())
      setThinking(true)
      if (activeSessionRef.current) {
        // active session already minted — surface it in the drawer right away
        optimisticInsert(activeSessionRef.current, trimmed)
      } else {
        // session id not minted yet; onReady will insert once it arrives
        pendingTitleRef.current = trimmed
      }
      if (statusRef.current === 'ready' && clientRef.current) clientRef.current.send(trimmed, uuid())
      else queueRef.current.push(trimmed)
    },
    [connect, thinking, optimisticInsert]
  )

  const teardown = useCallback(() => {
    if (clientRef.current) clientRef.current.close()
    clientRef.current = null
    queueRef.current = []
    setThinking(false)
    setSteps([])
    setError(null)
  }, [])

  const newChat = useCallback(() => {
    teardown()
    activeSessionRef.current = null
    pendingTitleRef.current = null
    setActiveSessionId(null)
    setMessages([])
    connect(null)
  }, [teardown, connect])

  /* Load the caller's server-side conversation list (newest first, page 1). */
  const refreshHistory = useCallback(() => {
    setHistoryLoading(true)
    setHistoryError(null)
    listConversations({ page: 1 })
      .then((res) => {
        setSessions((prev) => {
          // preserve an optimistic stub for the active session if the server
          // hasn't persisted it yet (write-behind lag)
          const active = activeSessionRef.current
          const serverList = res.conversations || []
          if (active && !serverList.some((s) => s.session_id === active)) {
            const stub = prev.find((s) => s.session_id === active)
            if (stub) return [stub, ...serverList]
          }
          return serverList
        })
      })
      .catch((err) => setHistoryError(errorKind(err)))
      .finally(() => setHistoryLoading(false))
  }, [])

  const resumeSession = useCallback(
    (sessionId: string) => {
      if (sessionId === activeSessionRef.current && clientRef.current) return
      teardown()
      activeSessionRef.current = sessionId
      pendingTitleRef.current = null
      setActiveSessionId(sessionId)
      setMessages([])
      setHistoryError(null)
      // load the past transcript over REST, then resume the WS on that session
      getFullTranscript(sessionId)
        .then((transcript) => {
          // ignore if the user switched sessions while this was in flight
          if (activeSessionRef.current !== sessionId) return
          setMessages(transcriptToMessages(transcript))
          connect(sessionId)
        })
        .catch((err) => {
          if (activeSessionRef.current !== sessionId) return
          setHistoryError(errorKind(err))
        })
    },
    [teardown, connect]
  )

  const deleteSession = useCallback(
    (sessionId: string) => {
      // optimistic removal; reconcile the list back on failure
      const snapshot = sessions
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId))
      deleteConversation(sessionId).catch((err) => {
        setSessions(snapshot)
        setHistoryError(errorKind(err))
      })
      if (sessionId === activeSessionRef.current) newChat()
    },
    [sessions, newChat]
  )

  /* close the socket when the widget unmounts */
  useEffect(() => () => teardown(), [teardown])

  return {
    status,
    messages,
    steps,
    thinking,
    error,
    sessions,
    historyLoading,
    historyError,
    activeSessionId,
    connect,
    send,
    newChat,
    refreshHistory,
    resumeSession,
    deleteSession
  }
}
